import { isClientIncompatible } from './planner-api.ts'
import type { PlannerDateTime } from './planner-time.ts'
import { boundedVenueCandidates, distanceKilometres, searchCenters, type LocationPoint, type Venue } from './planning.ts'
import {
  rankEstimatedRoadVenues,
  type CandidatePriorityMode,
  type GroupRankedVenue,
  type RankingPreference,
} from './transit/candidates.ts'
import {
  candidateModeAreas, rankParticipantVenues, scheduledAlternatives,
  type EndpointJourney, type RoutedParticipant, type ScheduledRoutedPerson, type TravelPermissionSet,
} from './transit/modes.ts'
import type { ExactTransfer, TransferPass } from './transit/router.ts'

export interface SearchTime extends PlannerDateTime {
  intent: 'depart-at' | 'arrive-by'
}

export interface SearchRequest {
  participants: Array<{ point: LocationPoint; modes: TravelPermissionSet }>
  time: SearchTime
  discoveryTime: PlannerDateTime
  scheduleAvailable: boolean
  venueType: 'coffee' | 'brunch' | 'drinks'
  preference: RankingPreference
  area?: { center: { lat: number; lon: number }; radius: number }
}

export interface PlannedVenue extends GroupRankedVenue {
  journeys?: EndpointJourney[]
}

export type ScheduledBackend = 'subway' | 'bus' | 'ferry' | 'multimodal'
export type ExactTransfersByBackend = Partial<Record<ScheduledBackend, ExactTransfer[]>>
export type SearchPhaseId = 'walking' | 'trips' | 'connections' | 'areas' | 'venues' | 'travel-times' | 'ranking'
export type SearchProgressEvent = { phase: SearchPhaseId } | { failed: 'venues' }

export interface SearchRouting {
  routeGroup(pass: TransferPass, transfers: ExactTransfersByBackend): Promise<RoutedParticipant[]>
  routeSelectedTransfers(areas: Array<{ journeys?: EndpointJourney[] }>, people: ScheduledRoutedPerson[]): Promise<ExactTransfersByBackend>
  routeVenuePeople(people: RoutedParticipant[], venues: Venue[]): Promise<RoutedParticipant[]>
  readonly bikeShareUpdatedAt: number | null
}

interface VenueSearch {
  centers: Array<{ lat: number; lon: number }>
  radius: number
  type: SearchRequest['venueType']
}

export interface SearchDependencies {
  createRouting(request: SearchRequest, signal: AbortSignal): SearchRouting
  venues(query: VenueSearch, signal: AbortSignal): Promise<Venue[]>
}

interface SearchOutcome {
  people: RoutedParticipant[]
  bikeShareUpdatedAt: number | null
}

export type SearchResult = SearchOutcome & (
  | { kind: 'places'; venues: PlannedVenue[]; venueCandidates: number; degraded: boolean }
  | { kind: 'areas'; areas: PlannedVenue[]; radius: number; venueError: unknown }
  | { kind: 'empty'; reason: 'no_routes' | 'no_shared_area' | 'no_final_routes'; venueCandidates?: number }
)

function assertSearchActive(signal: AbortSignal): void {
  signal.throwIfAborted()
}

export function isSuperseded(cause: unknown): boolean {
  return cause instanceof Error && cause.message.startsWith('Superseded')
}

/** Runs the existing planner pipeline without browser state or presentation. */
export async function searchPlaces(
  input: SearchRequest,
  dependencies: SearchDependencies,
  signal: AbortSignal,
  progress: (event: SearchProgressEvent) => void = () => undefined,
): Promise<SearchResult> {
  const request: SearchRequest = {
    ...input,
    participants: input.participants.map(({ point, modes }) => ({ point: { ...point }, modes: [...modes] })),
    time: { ...input.time },
    discoveryTime: { ...input.discoveryTime },
    area: input.area ? { center: { ...input.area.center }, radius: input.area.radius } : undefined,
  }
  const stop = new AbortController()
  const active = AbortSignal.any([signal, stop.signal])
  const phase = (event: SearchProgressEvent) => {
    assertSearchActive(active)
    progress(event)
    assertSearchActive(active)
  }
  assertSearchActive(active)
  const routing = dependencies.createRouting(request, active)
  const points = request.participants.map(({ point }) => point)
  const outcome = (people: RoutedParticipant[]): SearchOutcome => ({
    people, bikeShareUpdatedAt: routing.bikeShareUpdatedAt,
  })
  try {
    let people = await routing.routeGroup('preliminary', {})
    assertSearchActive(active)
    const wholeJourneyAllowed = people.some((person) => person.permissions.some((mode) =>
      mode === 'walk' || mode === 'taxi' || mode === 'bike' || mode === 'bike-share'))
    let scheduledPeople = people.map(scheduledAlternatives)
    let areas = candidateModeAreas(scheduledPeople.flat(), request.preference, null, wholeJourneyAllowed ? 2 : 3)
    if (scheduledPeople.some((alternatives) => alternatives.length)) {
      phase({ phase: 'connections' })
      const transfers = await routing.routeSelectedTransfers(areas, scheduledPeople.flat())
      assertSearchActive(active)
      people = await routing.routeGroup('final', transfers)
      assertSearchActive(active)
      scheduledPeople = people.map(scheduledAlternatives)
      areas = candidateModeAreas(scheduledPeople.flat(), request.preference, null, wholeJourneyAllowed ? 2 : 3)
    }
    phase({ phase: 'areas' })
    if (people.some((person) => !person.alternatives.length)) {
      return { ...outcome(people), kind: 'empty', reason: 'no_routes' }
    }
    if (!request.area && !areas.length && !wholeJourneyAllowed) {
      return { ...outcome(people), kind: 'empty', reason: 'no_shared_area' }
    }
    const center = {
      lat: points.reduce((sum, point) => sum + point.lat, 0) / points.length,
      lon: points.reduce((sum, point) => sum + point.lon, 0) / points.length,
    }
    const centers = request.area ? [request.area.center] : searchCenters(areas, center, wholeJourneyAllowed)
    const radius = request.area ? request.area.radius : areas.length ? 900 : 1500
    phase({ phase: 'venues' })
    let providerVenues: Venue[]
    try {
      providerVenues = await dependencies.venues({ centers, radius, type: request.venueType }, active)
      assertSearchActive(active)
    } catch (cause) {
      assertSearchActive(active)
      if (isClientIncompatible(cause) || isSuperseded(cause)) throw cause
      phase({ failed: 'venues' })
      if (request.area) throw cause
      const candidates: Venue[] = centers.map((point, index) => ({
        id: `search-area-${index}`, name: `Area ${index + 1}`, type: 'candidate', ...point,
      }))
      try {
        people = await routing.routeVenuePeople(people, candidates)
        assertSearchActive(active)
      } catch (fallbackCause) {
        assertSearchActive(active)
        if (isClientIncompatible(fallbackCause) || isSuperseded(fallbackCause)) throw fallbackCause
        throw cause
      }
      const ranked = rankParticipantVenues(candidates, people, request.preference, null, true)
      if (!ranked.length) throw cause
      return { ...outcome(people), kind: 'areas', areas: ranked, radius, venueError: cause }
    }
    if (request.area) {
      const { center, radius } = request.area
      providerVenues = providerVenues.filter(venue => distanceKilometres(center, venue) <= radius / 1000)
    }
    const venues = boundedVenueCandidates(
      providerVenues, centers, 25,
      rankEstimatedRoadVenues(providerVenues, points, people.map((person) => person.permissions.filter(
        (mode): mode is CandidatePriorityMode => mode === 'walk' || mode === 'bike' || mode === 'taxi',
      )), request.preference),
    )
    phase({ phase: 'travel-times' })
    people = await routing.routeVenuePeople(people, venues)
    assertSearchActive(active)
    if (people.some((person) => !person.alternatives.length)) {
      return { ...outcome(people), kind: 'empty', reason: 'no_final_routes', venueCandidates: providerVenues.length }
    }
    phase({ phase: 'ranking' })
    const ranked = rankParticipantVenues(venues, people, request.preference, null, true).slice(0, 8)
    return {
      ...outcome(people), kind: 'places', venues: ranked, venueCandidates: providerVenues.length,
      degraded: people.some((person) => person.failures.length > 0),
    }
  } catch (cause) {
    stop.abort(cause)
    throw cause
  }
}
